import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Utensils } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';


export default function MacroTracker() {
  const [macros, setMacros] = useState([]);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    protein_g: '',
    carbs_g: '',
    fats_g: '',
    notes: ''
  });

  useEffect(() => {
    fetchMacros();
  }, []);

  const fetchMacros = async () => {
    try {
      const response = await fetch(`${API}/tracking/macros`, {
        credentials: 'include'
      });
      if (response.ok) {
        setMacros(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API}/tracking/macros`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          protein_g: parseFloat(formData.protein_g),
          carbs_g: parseFloat(formData.carbs_g),
          fats_g: parseFloat(formData.fats_g)
        })
      });

      if (response.ok) {
        toast.success('Macros logged');
        setFormData({
          date: new Date().toISOString().split('T')[0],
          protein_g: '',
          carbs_g: '',
          fats_g: '',
          notes: ''
        });
        fetchMacros();
      }
    } catch (error) {
      toast.error('Failed to log macros');
    }
  };

  const chartData = macros.slice(0, 14).reverse().map(m => ({
    date: new Date(m.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    Protein: m.protein_g,
    Carbs: m.carbs_g,
    Fats: m.fats_g
  }));

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="macro-tracker-page">
      <h1 className="text-4xl font-bold mb-8">Macro Tracker</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6 glass-card">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Utensils className="h-6 w-6 text-primary" />
            Log Macros
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Protein (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.protein_g}
                  onChange={(e) => setFormData({...formData, protein_g: e.target.value})}
                  placeholder="150"
                  required
                />
              </div>
              <div>
                <Label>Carbs (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.carbs_g}
                  onChange={(e) => setFormData({...formData, carbs_g: e.target.value})}
                  placeholder="200"
                  required
                />
              </div>
              <div>
                <Label>Fats (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.fats_g}
                  onChange={(e) => setFormData({...formData, fats_g: e.target.value})}
                  placeholder="70"
                  required
                />
              </div>
            </div>

            <Button type="submit" className="w-full">Log Macros</Button>
          </form>
        </Card>

        <Card className="p-6 glass-card">
          <h2 className="text-2xl font-bold mb-6">Today's Summary</h2>
          {macros.length > 0 && macros[0].date === new Date().toISOString().split('T')[0] ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/30 text-center">
                  <p className="text-2xl font-bold text-primary">{macros[0].protein_g}g</p>
                  <p className="text-sm text-muted-foreground">Protein</p>
                </div>
                <div className="p-4 rounded-lg bg-success/10 border border-success/30 text-center">
                  <p className="text-2xl font-bold text-success">{macros[0].carbs_g}g</p>
                  <p className="text-sm text-muted-foreground">Carbs</p>
                </div>
                <div className="p-4 rounded-lg bg-warning/10 border border-warning/30 text-center">
                  <p className="text-2xl font-bold text-warning">{macros[0].fats_g}g</p>
                  <p className="text-sm text-muted-foreground">Fats</p>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-info/10 border border-info/30 text-center">
                <p className="text-3xl font-bold text-info">{macros[0].calories}</p>
                <p className="text-sm text-muted-foreground">Calories</p>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-12">No macros logged today</p>
          )}
        </Card>
      </div>

      {macros.length > 0 && (
        <Card className="p-6 glass-card">
          <h2 className="text-2xl font-bold mb-6">Macro Trends (Last 14 Days)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="date" stroke="#A1A1AA" fontSize={12} />
              <YAxis stroke="#A1A1AA" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#18181B',
                  border: '1px solid #3B82F6',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Bar dataKey="Protein" fill="#3B82F6" />
              <Bar dataKey="Carbs" fill="#10B981" />
              <Bar dataKey="Fats" fill="#F59E0B" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}
